import { computed, IObservableArray, makeObservable, observable } from 'mobx';
import { OptimisticPatch } from './CollectionOptimisticPatch';

export interface CollectionOptimisticActionsStateParams<T> {
  readonly patchesCache: Map<string, IObservableArray<OptimisticPatch<T>>>;
  readonly source: { queryName: string };
  readonly cacheNamespace?: string;
}

export class CollectionOptimisticActionsState<T> {
  readonly patches;

  _patchesBeforeUnmount: OptimisticPatch<T>[] | null = null;

  constructor(params: CollectionOptimisticActionsStateParams<T>) {
    const {
      source: { queryName },
      patchesCache,
      cacheNamespace,
    } = params;

    const cacheKey = [cacheNamespace, queryName]
      .filter((e) => e != null)
      .join(':');
    let patches = patchesCache.get(cacheKey);

    if (patches == null) {
      patches = observable.array<OptimisticPatch<T>>([], {
        deep: false,
      });
      patchesCache.set(cacheKey, patches);
    }

    this.patches = patches;

    makeObservable(this, {
      latestMap: computed,
    });
  }

  get latestMap() {
    return this.patches.reduce((map, optimisticPatch) => {
      const { patchedItemsEntries, originalItems } = optimisticPatch;
      for (const [key, item] of patchedItemsEntries) {
        const originalItem = originalItems.get(key);
        if (originalItem) {
          map.set(key, { optimisticPatch, item, originalItem });
        }
      }
      return map;
    }, new Map<string, { item: T; originalItem: T; optimisticPatch: OptimisticPatch<T> }>());
  }
}
